import RespImage from '@/app/_components/core/resp-image';
import { SmartLink } from '@/app/_components/core/smart-link';
import type { CarouselImage } from '@/app/_components/shared/carousel/carousel.types';
import {
  isPortraitIndex,
  buildImageSizes,
  buildArtDirectedSrcset,
  getAspectRatioClass,
} from '@/app/_components/shared/carousel/carousel.utils';
import { cn, emptyToUndefined } from '@/app/_lib/utils';

interface CarouselImageItemProps {
  image: CarouselImage;
  index: number;
  layoutSplit?: boolean;
  showCaption?: boolean;
  slidesPerView?: number;
  displayType?: string;
  priority?: boolean;
  trackingPrefix?: string;
}

export function CarouselImageItem({
  image,
  index,
  layoutSplit = false,
  showCaption = true,
  slidesPerView = 4,
  displayType = 'lp',
  priority,
  trackingPrefix,
}: CarouselImageItemProps) {
  const isPortrait = layoutSplit && isPortraitIndex(index);

  const { srcsetCandidates, artDirection } = buildArtDirectedSrcset(
    image,
    layoutSplit,
    isPortrait,
    slidesPerView,
    displayType
  );

  // If art direction is used, sizes is only for mobile/tablet (fallback source)
  // Desktop source has its own sizes set in buildArtDirectedSrcset
  // If no art direction, sizes covers all breakpoints
  const sizes = artDirection
    ? buildImageSizes(slidesPerView, displayType, isPortrait, 'mobile')
    : buildImageSizes(slidesPerView, displayType, isPortrait, 'all');

  const aspectClass = getAspectRatioClass(displayType, isPortrait);
  const imageContent = (
    <div
      className={cn(aspectClass, 'overflow-hidden rounded-carousel-image-radius')}
    >
      <RespImage
        srcsetCandidates={srcsetCandidates}
        artDirection={artDirection}
alt={emptyToUndefined(image.altText) ?? emptyToUndefined(image.title) ?? emptyToUndefined(image.caption) ?? ''}
        title={image.title}
        sizes={sizes}
        className="size-full  object-cover"
        objectFit="cover"
        priority={priority}
      />
    </div>
  );

  const imageWithOptionalLink = image.link ? (
    <SmartLink
      href={image.link}
      className="block"
      data-track-id={trackingPrefix}
    >
      {imageContent}
    </SmartLink>
  ) : (
    imageContent
  );

  return (
    <div className="flex flex-col gap-3">
      {imageWithOptionalLink}
      {showCaption && image.caption && (
        <p className="w-full truncate indent-3 text-sm text-muted-foreground">
          {image.caption}
        </p>
      )}
    </div>
  );
}

export { isPortraitIndex };
